import { faHouse } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { StayListingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Tabs,
} from '../../components/ui';
import { useMyStaysQuery } from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { listingEditPath, listingManagePath } from './ListingWizardPage';
import { stayErrorMessage } from './stayErrors';
import {
  FILTERS,
  LISTING_STATUS_LABELS,
  matchesFilter,
  type ListingFilter,
  isDraftStatus,
  listingSummary,
  resumeWizardStep,
  type StayListing,
} from './stayForm';

const Grid = styled.div`
  ${tw`grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3`}
`;

const ListingButton = styled.button`
  ${tw`flex flex-col w-full p-0 text-left cursor-pointer`}
  border: none;
  background: none;
  color: inherit;
`;

const Cover = styled.div`
  ${tw`relative w-full overflow-hidden`}
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  aspect-ratio: 4 / 3;
  background: var(--color-bg-secondary);
  img {
    ${tw`object-cover w-full h-full`}
  }
`;

const CoverBadge = styled.div`
  ${tw`absolute`}
  top: 12px;
  left: 12px;
`;

const CardText = styled.div`
  ${tw`flex flex-col gap-1 p-4`}
`;

function ListingCard({ listing, onOpen }: { listing: StayListing; onOpen: () => void }) {
  const status = listing.stayDetails?.listingStatus ?? StayListingStatus.Draft;
  const label = LISTING_STATUS_LABELS[status];
  const cover = listing.images?.[0]?.imageUrl;
  return (
    <Card $interactive data-testid={`stay-listing-${listing.id}`}>
      <ListingButton type="button" onClick={onOpen}>
        <Cover>
          {cover ? <img src={toPublicS3ObjectUrl(cover)} alt="" loading="lazy" /> : null}
          <CoverBadge>
            <Badge tone={label.tone} dot>
              {label.label}
            </Badge>
          </CoverBadge>
        </Cover>
        <CardText>
          <Strong>{listing.name || 'Untitled place'}</Strong>
          <Muted $size="sm">{listingSummary(listing)}</Muted>
          <Muted $size="sm">
            {isDraftStatus(status)
              ? 'Continue where you left off'
              : listing.weekdayPrice
                ? `$${listing.weekdayPrice} night`
                : 'No price yet'}
          </Muted>
        </CardText>
      </ListingButton>
    </Card>
  );
}

/** The host's places on the stays site (spec 016 W7). */
export default function ListingsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ListingFilter>('all');
  const { data, loading, error } = useMyStaysQuery({
    variables: { page: 1, limit: 50 },
    fetchPolicy: 'cache-and-network',
  });
  const listings = data?.myStays.items ?? [];
  const shown = listings.filter(listing =>
    matchesFilter(listing.stayDetails?.listingStatus, filter),
  );

  const open = (listing: StayListing) =>
    navigate(
      isDraftStatus(listing.stayDetails?.listingStatus)
        ? listingEditPath(listing.id, resumeWizardStep(listing.stayDetails?.wizardStep))
        : listingManagePath(listing.id),
    );

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Hosting"
        title="Your listings"
        actions={
          <Button
            type="button"
            text="Create listing"
            onClick={() => navigate('/stays/host/listings/new')}
            dataTestId="stay-create-listing"
          />
        }
      />
      <Stack $gap={6}>
        <Tabs
          items={FILTERS.map(item => ({ key: item.key, label: item.label }))}
          value={filter}
          onChange={setFilter}
        />
        {error && !data && (
          <Alert tone="error" title="Could not load your listings">
            {stayErrorMessage(error)}
          </Alert>
        )}
        {loading && !data ? (
          <LoadingBlock label="Loading your listings" />
        ) : shown.length === 0 ? (
          <Card>
            <EmptyState
              icon={faHouse}
              title={listings.length === 0 ? 'List your first place' : 'Nothing here'}
              description={
                listings.length === 0
                  ? 'Answer a few questions, add photos and publish when you are ready.'
                  : 'No listings match this filter.'
              }
            />
          </Card>
        ) : (
          <Grid>
            {shown.map(listing => (
              <ListingCard key={listing.id} listing={listing} onOpen={() => open(listing)} />
            ))}
          </Grid>
        )}
      </Stack>
    </PageContainer>
  );
}
